import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { EMPTY, catchError, filter, switchMap } from 'rxjs';
import { ResourceTypes } from '@go-modules/enums/archive';
import { ArchivedListDataSource } from '@go-modules/services/archive';
import { NgxSessionService } from '@go-modules/services/session/session.service';
import { ConfirmDialogComponent, ConfirmDialogData } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { NgxCourseService } from '@go-modules/services/course/course.service';
import { GoToastStatusType } from '@go-modules/enums/go-toast-status-type';
import { NgxGoToastService } from '@go-modules/services/go-toast/go-toast.service';
import { FolderListDataSource } from '@go-modules/services/folder-list-datasource/folder-list-datasource';
import { ActivityListDataSource } from '@go-modules/services/activity-list-datasource/activity-list.datasource';
import { SessionListDataSource } from '@go-modules/services/session-list-datasource/session-list.datasource';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { ENVIRONMENTS } from '@go-modules/services/environment-vars/environments';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import { MessageDialogComponent } from '@go-modules/components/dialogs/message-dialog/message-dialog.component';

interface RestoreResponse {
	parent_id: number;
	parent_name: string;
}

@Component({
	selector: 'archive-menu',
	templateUrl: './archive-menu.component.html',
	styleUrls: ['./archive-menu.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [TranslatePipe],
	standalone: false
})
export class ArchiveMenuComponent {
	@Input() public archived;
	private environmentVarsService: EnvironmentVarsService;

	constructor (
		private dialog: MatDialog,
		private translatePipe: TranslatePipe,
		private sessionService: NgxSessionService,
		private ngxGoToastService: NgxGoToastService,
		private activityService: NgxActivityService,
		private courseService: NgxCourseService,
		private archiveListDataSource: ArchivedListDataSource,
		private folderListDataSource: FolderListDataSource,
		private activityListDataSource: ActivityListDataSource,
		private sessionListDataSource: SessionListDataSource,
		private routerService: RouterService
	) {
		this.environmentVarsService = EnvironmentVarsService.getInstance();
	}

	public isLti (): boolean {
		return this.environmentVarsService.environmentIs(ENVIRONMENTS.LTI);
	}

	public restore () {
		let request;
		switch (this.archived.resource_type) {
			case ResourceTypes.SESSION:
				request = this.sessionService.restore(this.archived.resource_id);
				break;
			case ResourceTypes.ACTIVITY:
				request = this.activityService.restore(this.archived.resource_id);
				break;
			default:
				request = this.courseService.restoreCourse(this.archived.resource_id);
		}
		request.subscribe({
			next: (response: RestoreResponse) => {
				this.archiveListDataSource.remove(this.archived);
				const actionLink = this.createActionLink(this.archived.resource_type, response);
				switch (this.archived.resource_type) {
					case ResourceTypes.SESSION:
						this.sessionListDataSource.reload();
						break;
					case ResourceTypes.ACTIVITY:
						this.activityListDataSource.reload();
						break;
					default:
						this.folderListDataSource.reload();
				}
				if (this.isLti()) {
					const message = this.translatePipe.transform('archive-menu_restore-success-no-link', {name: response.parent_name});
					this.ngxGoToastService.createToast({
						type: GoToastStatusType.SUCCESS,
						message
					});
				} else {
					this.ngxGoToastService.createToast({
						type: GoToastStatusType.SUCCESS,
						message: 'archive-menu_restore-success',
						actionLink
					});
				}
			},
			error: (error) => {
				if (error.status === 422 &&
					[ResourceTypes.ACTIVITY, ResourceTypes.SESSION].includes(this.archived.resource_type)) {
					this.ngxGoToastService.createToast({
						type: GoToastStatusType.ERROR,
						message: this.archived.resource_type === ResourceTypes.SESSION ?
							'archive-menu_restore-unprocessable_error' :
							'archive-menu_restore-unprocessable_error-activity'
					});
				} else {
					this.ngxGoToastService.createToast({
						type: GoToastStatusType.ERROR,
						message: 'archive-menu_restore-error'
					});
				}
			}
		});
	}

	public createActionLink (resourceType: ResourceTypes, response: RestoreResponse) {
		switch (resourceType) {
			case ResourceTypes.SESSION:
				return {
					title: this.translatePipe.transform('archive-menu_restore-destination', {name: response.parent_name}),
					action: () => this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW, {
						activity_id: response.parent_id
					})
				};
			case ResourceTypes.ACTIVITY:
				return {
					title: this.translatePipe.transform('archive-menu_restore-destination', {name: response.parent_name}),
					action: () => this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_FOLDER_VIEW, {
						folder_id: response.parent_id
					})
				};
			case ResourceTypes.GROUP:
				return {
					title: this.translatePipe.transform('archive-menu_restore-destination', {name: response.parent_name}),
					action: () => this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_FOLDERS, {id: response.parent_id})
				};
			default:
				throw new Error('Unsupported resource type');
		}
	}

	public deletePermanently () {
		const dialogRef: MatDialogRef<ConfirmDialogComponent, any> = ConfirmDialogComponent.open(this.dialog, {
			data: {
				title: this.translatePipe.transform('common_delete-permanently'),
				message: this.translatePipe.transform('archive-permanent-delete_message', {name: this.getDeleteItemTitle()}),
				confirmText: this.translatePipe.transform('common_permanently-delete')
			} as ConfirmDialogData
		});

		dialogRef.afterClosed().pipe(
			filter((confirm: boolean) => confirm),
			switchMap(() => {
				switch (this.archived.resource_type) {
					case ResourceTypes.SESSION:
						return this.sessionService.delete(this.archived.resource_id);
					case ResourceTypes.ACTIVITY:
						return this.activityService.deleteActivity(this.archived.resource_id);
					default:
						return this.deleteCourse();
				}
			})
		).subscribe(() => this.archiveListDataSource.remove(this.archived));
	}

	private deleteCourse () {
		return this.courseService.deleteCourse(this.archived.resource_id).pipe(
			catchError((error) => {
				if (error.status === 409) {
					MessageDialogComponent.open(this.dialog, {
						data: {
							title: this.translatePipe.transform('archive-permanent-delete_error_title'),
							message: this.translatePipe.transform('archive-permanent-delete_error_message')
						}
					});
				}
				return EMPTY;
			})
		);
	}

	private getDeleteItemTitle (): string {
		return this.archived.name !== null ?
			`<strong>${this.archived.name}</strong>` :
			this.translatePipe.transform('archive-permanent-delete_message-no-name');
	}
}
